import { Directive, effect, EmbeddedViewRef, inject, input, TemplateRef, ViewContainerRef } from '@angular/core';
import type { AuthUser, RealUser } from '../state/auth';

interface RealUserContext {
  realUser: RealUser;
}

/**
 * Renders its template only for a signed-in (non-anonymous) user, narrowing
 * `AuthUser` down to `RealUser` on the way in.
 *
 * ```html
 * <div *realUser="user() as user; else Anonymous">{{ user.name }}</div>
 * <ng-template #Anonymous>...</ng-template>
 * ```
 */
@Directive({ selector: '[realUser]' })
export class RealUserDirective {

  private readonly template = inject<TemplateRef<RealUserContext>>(TemplateRef);
  private readonly container = inject(ViewContainerRef);

  readonly user = input.required<AuthUser>({ alias: 'realUser' });
  readonly otherwise = input<TemplateRef<void> | null>(null, { alias: 'realUserElse' });

  private view: EmbeddedViewRef<RealUserContext> | null = null;

  /**
   * which template is on screen - the branch alone is not enough, because
   * anonymous users change identity without changing branch (signing out mints
   * a fresh one) and re-creating the view for that would throw away dom state
   */
  private rendered: TemplateRef<unknown> | null = null;

  constructor() {
    effect(() => {
      const user = this.user();
      const otherwise = this.otherwise();

      if (user.isAnonymous) {
        if (this.rendered === otherwise)
          return;

        this.view = null;
        this.container.clear();
        if (otherwise)
          this.container.createEmbeddedView(otherwise);
        this.rendered = otherwise;
      }
      else if (this.view && this.rendered === this.template) {
        // same view, fresh user — updating the context is enough
        this.view.context.realUser = user;
        this.view.markForCheck();
      }
      else {
        this.container.clear();
        this.view = this.container.createEmbeddedView(this.template, {
          realUser: user
        });
        this.rendered = this.template;
      }
    });
  }

  static ngTemplateContextGuard(
    directive: RealUserDirective,
    context: unknown
  ): context is RealUserContext {
    return true;
  }
}
